import { Typography } from '@mui/material'
import {
  DataGrid,
  type DataGridProps,
  type GridColumns,
  GridOverlay,
  type GridRowParams
} from '@mui/x-data-grid'
import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  getTeamExternalIntegrationRow,
  getTeamExternalIntegrationRowIdentifier,
  type TeamExternalIntegrationRow
} from 'components/status-checks/common/team-integration-table/team-integration-table-utils'
import { TeamIntegrationTableActions } from 'components/status-checks/common/team-integration-table/TeamIntegrationTableActions'
import { TeamIntegrationTableName } from 'components/status-checks/common/team-integration-table/TeamIntegrationTableName'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import { useStatusChecksSearchContext } from 'components/status-checks/StatusChecksSearchState'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import useAuth from 'hooks/use-auth'
import { type TeamExternalIntegration } from 'types/api/status-checks/status-check'
import { externalIntegrationsStatusChecksPut } from 'utils/endpoints-access'
import {
  defaultColumnProps,
  defaultGridProps,
  getGridStyles,
  safeRowClick
} from 'utils/mui-components/data-grid-utils'

const EmptyState: React.FC = () => {
  return (
    <GridOverlay>
      <Typography>No Authentications</Typography>
    </GridOverlay>
  )
}

export function TeamIntegrationTable({
  hasLogoOnIntegration = true,
  headerName,
  isThirdPartyIntegrationsTable = false,
  onChange,
  savedIntegrations
}: {
  hasLogoOnIntegration?: boolean
  headerName?: string
  isThirdPartyIntegrationsTable?: boolean
  onChange?: () => void
  savedIntegrations: TeamExternalIntegration[]
}) {
  const { hasEndpointAccess } = useAuth()

  const { evaluatedTeamId } = useStatusCheckExternalIntegrationsContext()

  const canPutExternalIntegrationsStatusChecks = hasEndpointAccess(
    externalIntegrationsStatusChecksPut,
    {
      teamId: evaluatedTeamId
    }
  )

  const navigate = useNavigate()
  const statusChecksUrls = getStatusChecksUrls()

  const teamExternalIntegrationRows = useMemo(
    () => savedIntegrations.map(getTeamExternalIntegrationRow),
    [savedIntegrations]
  )

  const searchState = useStatusChecksSearchContext()
  const { pageNum, setPageNum } = searchState.authentications

  const handleRowClick: DataGridProps['onRowClick'] = (
    { row }: GridRowParams<TeamExternalIntegrationRow>,
    event
  ) => {
    if (isThirdPartyIntegrationsTable) return

    safeRowClick(event, () => {
      navigate(
        statusChecksUrls.editAuthenticationFromTeamExternalIntegration(row)
      )
    })
  }

  const tableColumns: GridColumns<TeamExternalIntegrationRow> = [
    {
      ...defaultColumnProps,
      field: 'displayName',
      headerName: headerName ?? 'Observability Tool',
      flex: 3,
      renderCell: ({ row }) => {
        return (
          <TeamIntegrationTableName
            hasLogoOnIntegration={hasLogoOnIntegration}
            integration={row}
            isThirdPartyIntegrationsTable={isThirdPartyIntegrationsTable}
            savedIntegrations={savedIntegrations}
          />
        )
      }
    },
    {
      ...defaultColumnProps,
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      renderCell: ({ id, row }) => {
        return (
          <TeamIntegrationTableActions
            id={id}
            integration={row}
            isThirdPartyIntegrationsTable={isThirdPartyIntegrationsTable}
            savedIntegrations={savedIntegrations}
            onChange={onChange}
          />
        )
      }
    }
  ]

  return (
    <DataGrid<TeamExternalIntegrationRow>
      {...defaultGridProps}
      columns={tableColumns}
      columnVisibilityModel={{
        actions: isThirdPartyIntegrationsTable
          ? canPutExternalIntegrationsStatusChecks
          : true
      }}
      components={{
        NoRowsOverlay: EmptyState
      }}
      getRowId={getTeamExternalIntegrationRowIdentifier}
      page={pageNum}
      rows={teamExternalIntegrationRows}
      sx={getGridStyles({
        clickableRow: !isThirdPartyIntegrationsTable
      })}
      onPageChange={setPageNum}
      onRowClick={handleRowClick}
    />
  )
}
